import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Glasses,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { SITE_CONTACT, whatsappUrl } from "@/config/site-contact";
import { useLogout, useSwitchOrg } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";
import type { MeResponse } from "@/features/auth/types";
import { formatDateFr } from "@/lib/utils";

const PLAN_LABELS: Record<string, string> = {
  trial: "Essai",
  standard: "Standard",
  premium: "Premium",
};

/** Écran de blocage affiché aux utilisateurs d'une clinique dont la licence ne
 *  couvre plus l'accès (le back refuse déjà les endpoints clinique, cf.
 *  `apps.licensing.middleware`). La régularisation est faite par GoldenTech
 *  depuis la console éditeur : cet écran ne propose donc aucune action de
 *  paiement, mais il ne laisse pas non plus l'utilisateur dans une impasse.
 *
 *  Composition : un bandeau d'état (motif exact, clinique, échéance), puis ce
 *  qu'il faut faire (contacter l'éditeur), puis les issues (revérifier après
 *  régularisation, changer de clinique, se déconnecter). Une seule action mise
 *  en avant — le contact WhatsApp, canal réel du support en Guinée ; les
 *  coordonnées sont celles du pied de page public (madagn.com).
 *
 *  Trois choses manquaient à la version initiale, toutes coûteuses en support :
 *   1. le motif exact — une licence SUSPENDUE n'est pas une licence expirée, et
 *      annoncer « expirée » à échéance encore valide décrédibilise le message ;
 *   2. le moyen de contacter GoldenTech, alors que le texte le demandait ;
 *   3. une sortie autre que la déconnexion — vérifier après régularisation, et
 *      basculer sur une autre clinique quand l'utilisateur en a plusieurs
 *      (`/auth/` est en liste blanche du middleware, la bascule fonctionne).
 */
export function LicenseExpiredScreen() {
  const me = useAuthStore((s) => s.me);
  const logout = useLogout();
  const switchOrg = useSwitchOrg();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);

  const lic = me?.license ?? null;
  const suspended = lic?.status === "suspended";
  const overdueDays = lic ? Math.max(0, -lic.days_remaining) : 0;
  const clinic = me?.memberships.find((m) => m.organisation_id === me.active_organisation_id);
  const clinicName = clinic?.organisation_name ?? "Clinique";
  const otherClinics = (me?.memberships ?? []).filter(
    (m) => m.organisation_id !== me?.active_organisation_id,
  );

  const contactSubject = `Licence MADA — ${clinicName}`;
  const contactMessage = `Bonjour, l'accès à MADA est bloqué pour ${clinicName} (licence ${suspended ? "suspendue" : "expirée"}). Merci de nous aider à régulariser.`;

  /** Après régularisation par GoldenTech, éviter le cycle déconnexion /
   *  reconnexion : on relit `/auth/me` (endpoint non bloqué) et l'écran
   *  disparaît de lui-même si la licence est redevenue valide. */
  async function recheck() {
    setChecking(true);
    try {
      await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      const fresh = queryClient.getQueryData<MeResponse>(["auth", "me"]);
      if (fresh?.license && !fresh.license.is_valid) {
        toast.info("La licence n'est pas encore réactivée.");
      }
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* L'écran remplace la navbar : il porte sa propre identité, sinon
          l'utilisateur perd tout repère au moment le plus anxiogène. */}
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-foreground">
          <Glasses className="h-5 w-5 text-primary" aria-hidden />
          <span className="font-semibold uppercase tracking-[0.2em]">MADA</span>
        </span>
        {me?.email && (
          <span className="max-w-[12rem] truncate text-xs text-muted-foreground">{me.email}</span>
        )}
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-3xl">
          <section className="overflow-hidden rounded border border-border bg-surface shadow-sm">
            {/* Bandeau d'état : le motif, la clinique concernée et les chiffres
                de la licence en une seule lecture. */}
            <div className="flex items-start gap-4 border-b border-destructive/20 bg-destructive/5 p-5 sm:p-6">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded bg-destructive/10 text-destructive">
                <ShieldAlert className="h-6 w-6" aria-hidden />
              </span>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-foreground sm:text-xl">
                  {suspended ? "Licence suspendue" : "Licence expirée"}
                </h1>
                <p className="mt-0.5 truncate text-sm text-foreground">{clinicName}</p>
                {lic && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5 text-xs">
                    <span className="rounded border border-destructive/20 bg-surface px-2 py-0.5 text-muted-foreground">
                      Formule {PLAN_LABELS[lic.plan] ?? lic.plan}
                    </span>
                    <span className="rounded border border-destructive/20 bg-surface px-2 py-0.5 tabular-nums text-muted-foreground">
                      Échéance {formatDateFr(lic.expires_on)}
                    </span>
                    {overdueDays > 0 && (
                      <span className="rounded border border-destructive/30 bg-destructive/10 px-2 py-0.5 font-medium tabular-nums text-destructive">
                        Dépassée de {overdueDays} j
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Deux colonnes sur écran large : ce qui s'est passé à gauche,
                ce qu'il faut faire à droite. En une colonne le contenu tenait
                sur 500 px au milieu d'un écran vide. */}
            <div className="grid gap-5 p-5 sm:p-6 md:grid-cols-2 md:items-start">
              <div className="space-y-4 md:col-start-1 md:row-start-1">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {suspended
                    ? "L'accès aux données de la clinique est fermé jusqu'à la réactivation de la licence par GOLDENTECH SARL, éditeur de MADA."
                    : "La période de grâce est écoulée : l'accès aux données de la clinique est fermé jusqu'au renouvellement de la licence auprès de GOLDENTECH SARL, éditeur de MADA."}
                </p>

                {/* Première question du personnel devant un écran rouge : « a-t-on
                    perdu les dossiers ? ». Y répondre avant qu'elle soit posée. */}
                <p className="flex gap-2.5 rounded border border-border bg-background p-3 text-xs leading-relaxed text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 flex-none text-success" aria-hidden />
                  <span>
                    Aucune donnée n&apos;est supprimée. Dossiers patients, ventes et historiques
                    sont conservés et redeviennent accessibles dès la régularisation.
                  </span>
                </p>
              </div>

              <aside className="flex flex-col rounded border border-border bg-background p-4 md:col-start-2 md:row-start-1">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                  Régulariser avec GOLDENTECH SARL
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  L&apos;éditeur réactive la licence depuis sa console ; l&apos;accès revient sans
                  réinstallation ni ressaisie.
                </p>
                <Button className="mt-4 w-full" asChild>
                  <a href={whatsappUrl(contactMessage)} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="h-4 w-4" aria-hidden />
                    Écrire sur WhatsApp
                  </a>
                </Button>
                {/* Téléphone à gauche, email à droite : deux canaux de même
                    niveau, aux deux extrémités de la ligne. */}
                <ul className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-border pt-3 text-[13px]">
                  <li>
                    <a
                      href={`tel:${SITE_CONTACT.phoneTelHref}`}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap py-0.5 text-foreground transition-colors hover:text-primary"
                    >
                      <Phone className="h-4 w-4 flex-none text-muted" aria-hidden />
                      {SITE_CONTACT.phoneDisplay}
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${SITE_CONTACT.email}?subject=${encodeURIComponent(contactSubject)}`}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap py-0.5 text-foreground transition-colors hover:text-primary"
                    >
                      <Mail className="h-4 w-4 flex-none text-muted" aria-hidden />
                      {SITE_CONTACT.email}
                    </a>
                  </li>
                </ul>
              </aside>

              {otherClinics.length > 0 && (
                <div className="md:col-start-1 md:row-start-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                    Vos autres cliniques
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Accessibles si leur licence est à jour.
                  </p>
                  <ul className="mt-3 max-h-44 space-y-2 overflow-y-auto">
                    {otherClinics.map((m) => {
                      const busy =
                        switchOrg.isPending && switchOrg.variables === m.organisation_id;
                      return (
                        <li key={m.organisation_id}>
                          <button
                            type="button"
                            disabled={switchOrg.isPending}
                            onClick={() => switchOrg.mutate(m.organisation_id)}
                            className="flex w-full items-center gap-3 rounded border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <span className="flex h-8 w-8 flex-none items-center justify-center rounded bg-primary/10 text-primary">
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              ) : (
                                <Building2 className="h-4 w-4" aria-hidden />
                              )}
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-foreground">
                                {m.organisation_name}
                              </span>
                              <span className="block truncate font-mono text-[11px] uppercase text-muted">
                                {m.organisation_slug}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Issues utilitaires, séparées des actions de régularisation. */}
            <div className="flex flex-col gap-2 border-t border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <Button variant="outline" size="sm" onClick={recheck} disabled={checking}>
                {checking ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden />
                )}
                Vérifier à nouveau
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Se déconnecter
              </Button>
            </div>
          </section>

          <p className="mt-4 text-center text-xs text-muted">
            MADA — édité par GOLDENTECH SARL, {SITE_CONTACT.address}
          </p>
        </div>
      </main>
    </div>
  );
}
